import React from "react";
import { styled, Box } from "@mui/material";
// Patterns are now rendered from static .svg assets via a single CSS-mask Pattern
// component (scripts/extract-patterns.mjs) instead of 28 inline-JSX <svg> modules
// (~6.8 MB) compiled into the bundle. The `patterns` array below keeps its exact
// { id, component } shape so every consumer is unchanged.
import { Pattern } from "./children/Patterns/Pattern";

export const Container = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: -5px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f7f2fa;
`;

export const CornerHeading = styled("p")`
  font-size: 16px;
  color: #000000;
  font-family: "DM Sans", sans-serif;
  font-weight: 500;
  margin-right: 10px;
  margin-top: 8px;
`;

export const RowDiv = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const OverlayContainer = styled("div")`
  position: relative;
  width: 100%;
  height: 20vh;
  margin-top: 20px;
  background-color: ${(props) => props.bgColor || "rgba(67, 255, 100, 0.5)"};
  overflow: hidden;
`;

export const PatternImage = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const Overlay = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.bgColor || "rgba(67, 255, 100, 0.5)"};
  pointer-events: none;
`;

export const PatternGridContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
`;

export const GridContainer = styled("div")`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 20px;
  margin-top: 10px;
`;

export const PatternItemImage = styled("img")`
  height: 40px;
  width: 40px;
  object-fit: cover;
  margin: 2px;
  cursor: pointer;
`;

// Gordon 2026-08-19 (register 508): white/near-white swatches (e.g. a plain
// white Wallpaper image, or the empty/transparent state) were invisible against
// the side pane's own white background — no border at all. Adds a light-gray
// outline, universally, to every circular side-pane swatch so it reads on white.
const SWATCH_OUTLINE = "1px solid #e1e1e1";

export const SwatchOverlayImage = styled("div")`
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  border: ${SWATCH_OUTLINE};
  box-sizing: border-box;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    /* 100%/100% of the padding box (border-box parent), not a hardcoded 32px,
       so the overlay sits INSIDE the new outline instead of covering it. */
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${(props) => props.overlayColor || "rgba(0, 0, 0, 0.5)"};
    opacity: ${(props) => (props.isPattern ? 1 : 0)};
    pointer-events: none;
  }
`;

export const SwatchBackgroundImage = styled("img")`
  width: 32px;
  height: 32px;
  object-fit: cover;
`;

export const StyledRightIconContainer = styled("div")`
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  border: ${SWATCH_OUTLINE};
  box-sizing: border-box;
  overflow: hidden;
  background: ${(props) => props.bgColor || "transparent"};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledColorTypeSelector = styled("div")`
  cursor: pointer;
  width: 55px;
  height: 55px;
  border-radius: 30px;
  margin: 2.5px;
  border: ${(props) =>
    props.isSelected ? "2px solid #a8acb3" : "0.2px solid #e1e4e8"};
  display: flex;
  justify-content: center;
  align-items: center;

  > div {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    overflow: hidden;
    background-color: ${(props) => props.wallpaperOverlayColor};
  }
`;

export const StyledEmptyPattern = styled("div")`
  height: 40px;
  width: 40px;
  margin: 2px;
  cursor: pointer;
  border-radius: 4px;
  border: ${(props) => (props.isSelected ? "1px solid gray" : "none")};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f6f7f9;
  outline: none;
  &:focus-visible {
    outline: 2px solid #81459b;
    outline-offset: 2px;
  }
`;

export const StyledPatternContainer = styled("div")`
  height: 40px;
  width: 40px;
  overflow: hidden;
  margin: 2px;
  cursor: pointer;
  border-radius: 4px;
  border: ${(props) => (props.isSelected ? "1px solid gray" : "none")};
  background-color: #f6f7f9;
  outline: none;
  &:focus-visible {
    outline: 2px solid #81459b;
    outline-offset: 2px;
  }
`;

// Each entry's `component` is a thin wrapper that renders the shared mask-based
// Pattern for its id — same { id, component } contract the consumers already use
// (React.createElement(entry.component, { color })). Wrappers are created once at
// module load. id 0 keeps the sentinel "Empty" string (never createElement'd; the
// id===0 branches render an empty container).
export const patterns = [
  { id: 0, component: "Empty" },
  ...Array.from({ length: 28 }, (_, i) => {
    const id = i + 1;
    const PatternWrapper = (props) => React.createElement(Pattern, { id, ...props });
    PatternWrapper.displayName = `Pattern${id}`;
    return { id, component: PatternWrapper };
  }),
];
